import axios from "axios";

import i18n from "../i18n";
import { withAttribution } from "./attribution";

/**
 * Resolve the API base URL.
 *
 * Contract:
 *   - If `REACT_APP_BACKEND_URL` is an absolute URL (e.g.
 *     `https://api.example.com`), use it verbatim. This preserves the
 *     split-domain deployment shape (frontend on one host, backend on
 *     another).
 *   - If the env var is unset, empty, or just `/`, fall back to
 *     **same-origin relative paths** (`"/api"`). The browser treats the
 *     current document origin as the base, so `/api/foo` dispatches to
 *     the same host that served the SPA.
 *
 * Why this fallback exists (2026-04 post-mortem):
 *   `REACT_APP_BACKEND_URL` is read by Webpack at build time and frozen
 *   into the JS bundle. When the domain changes between environments
 *   (preview <-> live), the built bundle points at the *old* domain and
 *   magic-link clicks bounce users cross-origin, trashing cookies. The
 *   same-origin default means: don't set the env var, and the frontend
 *   always follows the domain it was served from. Zero rebuild needed on
 *   domain change. See DEPLOY.md.
 */
const BACKEND_URL_RAW = (process.env.REACT_APP_BACKEND_URL || "").trim();
const BACKEND_URL =
    !BACKEND_URL_RAW || BACKEND_URL_RAW === "/" ? "" : BACKEND_URL_RAW.replace(/\/+$/, "");
export const API = `${BACKEND_URL}/api`;

const http = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
    timeout: 20000,
});

const currentLocale = () =>
    (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);

/**
 * Decorate every submission with the current locale + any persisted
 * attribution stamp (`ref`, `src`) captured from the page URL.
 */
const decorate = (payload = {}) =>
    withAttribution({ ...payload, locale: currentLocale() });

// --- Phase 1 ---
export async function submitQuoteRequest(payload) {
    // Phase 1.5a — POST includes withCredentials so the server can issue
    // the proposal-session cookie on success.
    const res = await http.post("/quote-request", decorate(payload), {
        withCredentials: true,
    });
    return res.data;
}

// --- Phase 1.5a — proposal portal ---
export async function getProposal(proposalId) {
    const res = await http.get(`/portal/proposal/${proposalId}`, {
        withCredentials: true,
    });
    return res.data;
}

export async function requestPortalMagicLink({ email, locale }) {
    const res = await http.post(
        "/portal/proposal/request-magic-link",
        { email, locale: locale || currentLocale() },
    );
    return res.data;
}

export async function portalLogout() {
    const res = await http.post("/portal/proposal/logout", null, {
        withCredentials: true,
    });
    return res.data;
}

export async function patchProposalRiders(proposalId, riders) {
    const res = await http.patch(
        `/portal/proposal/${proposalId}/riders`,
        riders,
        { withCredentials: true },
    );
    return res.data;
}

export async function portalHeartbeat(proposalId) {
    const res = await http.post(
        `/portal/proposal/${proposalId}/heartbeat`,
        null,
        { withCredentials: true },
    );
    return res.data;
}

export async function startProposalApplication(proposalId) {
    const res = await http.post(
        `/portal/proposal/${proposalId}/start-application`,
        null,
        { withCredentials: true },
    );
    return res.data;
}

export async function employeeLookup(email, locale = "en") {
    const res = await http.post("/employee-lookup", { email, locale });
    return res.data;
}

export async function resolveInvitation(token) {
    const res = await http.get("/employee-invitations/resolve", {
        params: { token },
    });
    return res.data;
}

export async function expressInterest(token, action = "start_application") {
    const res = await http.post("/employee-invitations/express-interest", {
        token, action,
    });
    return res.data;
}

export async function hrContactRequest(payload) {
    const res = await http.post("/employee-invitations/hr-contact", payload);
    return res.data;
}

export async function nominateEmployer(payload) {
    // Phase 1.5d — public hub nominate (no apply-session required).
    const res = await http.post(
        "/employee-invitations/nominate-employer",
        payload,
    );
    return res.data;
}

export async function contact(payload) {
    const res = await http.post("/contact", payload);
    return res.data;
}

export async function getRatesForTier(coverageTier, classification) {
    const res = await http.get("/rates", {
        params: { tier: coverageTier, classification },
    });
    return res.data;
}

export async function inviteColleague(proposalId, recipientName, recipientEmail, note) {
    const res = await http.post(
        `/portal/proposal/${proposalId}/invite-colleague`,
        {
            recipient_name: recipientName,
            recipient_email: recipientEmail,
            note,
        },
        { withCredentials: true },
    );
    return res.data;
}

export async function submitEmployeeInterest(payload) {
    const res = await http.post("/employee-interest", decorate(payload));
    return res.data;
}

export async function submitContact(payload) {
    const res = await http.post("/contact", decorate(payload));
    return res.data;
}

// --- Phase 2: /apply ---
export async function verifyAccessCode(payload) {
    const res = await http.post("/apply/verify-code", decorate(payload));
    return res.data;
}

export async function searchEmployers(q, applySessionToken) {
    const headers = applySessionToken
        ? { Authorization: `Bearer ${applySessionToken}` }
        : {};
    const res = await http.get("/employers/search", {
        params: { q },
        headers,
    });
    return res.data;
}

export async function requestAccessCode(payload) {
    // Phase 2a: email-first endpoint (employer not yet known).
    const res = await http.post(
        "/apply/request-access-code",
        decorate(payload),
    );
    return res.data;
}

export async function nominateEmployerAuthed(payload, applySessionToken) {
    // Phase 2 — authed nominate flow (requires apply-session token).
    const headers = applySessionToken
        ? { Authorization: `Bearer ${applySessionToken}` }
        : {};
    const res = await http.post(
        "/apply/nominate-employer",
        decorate(payload),
        { headers },
    );
    return res.data;
}

// --- Phase 2: /share ---
export async function shareFriend(payload) {
    const res = await http.post("/share/friend", decorate(payload));
    return res.data;
}

export async function sharePeer(payload) {
    const res = await http.post("/share/peer", decorate(payload));
    return res.data;
}

// --- Phase 2a: employer magic-link auth (RETIRED in A6) ---
// Helper removed — /api/employer/request-magic-link returns 404 since A6.
// Use `employerPasswordSignin` below instead.

// --- Phase A2: password sign-in (employer + employee) ---
// Enumeration-safe: both unknown email and wrong password return 401
// with detail "invalid_credentials". A known email whose password has
// not been set yet returns 401 with detail "password_not_set".
// remember_me opts into the 30-day laguna_refresh cookie.
export async function employerPasswordSignin({ email, password, rememberMe }) {
    const res = await http.post(
        "/employer/signin",
        { email, password, remember_me: !!rememberMe },
        { withCredentials: true },
    );
    return res.data;
}

export async function employeePasswordSignin({ email, password, rememberMe }) {
    const res = await http.post(
        "/employee/signin",
        { email, password, remember_me: !!rememberMe },
        { withCredentials: true },
    );
    return res.data;
}

// --- Phase A3: password setup endpoints ---
// `validatePassword` is the inline strength/breach probe. Public, rate-
// limited 20/min/IP. Returns {valid, breached, length_ok}.
export async function validatePassword(password) {
    const res = await http.post(
        "/auth/password/validate",
        { password },
    );
    return res.data;
}
// passwordState reads whichever role-scoped session cookie is present
// and returns {has_password, context}. 401 if no session. Used by the
// /set-password page to decide whether to render the form or bounce.
export async function passwordState() {
    const res = await http.get(
        "/auth/password/state",
        { withCredentials: true },
    );
    return res.data;
}

// --- Phase A4: forgot / reset / setup-email ---
export async function passwordForgot({ role, email, locale }) {
    const res = await http.post(
        `/${role}/password/forgot`,
        { email, locale: locale || "en" },
    );
    return res.data;
}
export async function passwordReset({ role, token, newPassword }) {
    const res = await http.post(
        `/${role}/password/reset`,
        { token, new_password: newPassword },
        { withCredentials: true },
    );
    return res.data;
}
export async function passwordSetupEmail({ role, email, locale }) {
    const res = await http.post(
        `/${role}/password/setup-email`,
        { email, locale: locale || "en" },
    );
    return res.data;
}
export async function setEmployeePassword(newPassword) {
    const res = await http.post(
        "/employee/password/set",
        { new_password: newPassword },
        { withCredentials: true },
    );
    return res.data;
}
// Phase A5 — employer-session-authenticated password set, used by the
// forced-change flow after an admin-minted temp password.
export async function setEmployerPassword(newPassword) {
    const res = await http.post(
        "/employer/password/set",
        { new_password: newPassword },
        { withCredentials: true },
    );
    return res.data;
}
export async function setProposalPassword(newPassword) {
    const res = await http.post(
        "/proposal/password/set",
        { new_password: newPassword },
        { withCredentials: true },
    );
    return res.data;
}

// Phase 3e — fallback trigger when the post-quote password modal can't
// complete (e.g. network blip, session just expired). Requests that the
// backend email the user a setup link so they can still secure the
// account from their inbox. Enumeration-safe 200 whether the user row
// exists or not.
export async function requestEmployerSetupEmail(email) {
    const res = await http.post(
        "/employer/password/setup-email",
        { email, locale: currentLocale() },
    );
    return res.data;
}

// Phase 3e — list the signed-in email's saved quotes. Accepts either a
// proposal session (quote-only prospect) OR an employer session
// (post-application) — the backend resolves which cookie is present.
export async function employerQuotes() {
    const res = await http.get("/employer/quotes", { withCredentials: true });
    return res.data;
}

// Phase 3e — lightweight "another quote" from the dashboard. Only needs
// company + ZIP + employees; backend reuses the contact block from the
// signed-in user's most recent proposal.
export async function employerNewQuote(payload) {
    const res = await http.post(
        "/employer/quotes/new",
        payload,
        { withCredentials: true },
    );
    return res.data;
}

export async function employerMe() {
    const res = await http.get("/employer/me", { withCredentials: true });
    return res.data;
}

export async function employerCurrentApplication() {
    // Phase 2e §1 — used by /portal/{employer_id}/census to resolve the
    // active application regardless of lifecycle status.
    const res = await http.get("/employer/current-application", {
        withCredentials: true,
    });
    return res.data;
}

export async function employerSessionStatus() {
    // F11: Public session probe. Never 401s — nav uses this to toggle
    // Sign-in ↔ Sign-out on every route change without console noise.
    const res = await http.get("/employer/session", { withCredentials: true });
    return res.data;
}

export async function employerLogout() {
    const res = await http.post("/employer/logout", null, {
        withCredentials: true,
    });
    return res.data;
}

// --- Phase 2: landing tracker (raw http export for attribution helper) ---
export const rawHttp = {
    post: (path, body) => http.post(path, body),
};

export function formatApiError(err, fallback) {
    if (err?.response?.data?.detail) {
        const d = err.response.data.detail;
        if (Array.isArray(d)) {
            return d
                .map((x) => x.msg || x.message || JSON.stringify(x))
                .join(" · ");
        }
        if (typeof d === "object" && d.message) return d.message;
        return typeof d === "string" ? d : JSON.stringify(d);
    }
    return (
        err?.message ||
        fallback ||
        "Something went wrong. Try again or email hello@lagunabenefits.life."
    );
}

export function badRecipientIndices(err) {
    const bad = err?.response?.data?.detail?.bad_recipients;
    if (!Array.isArray(bad)) return [];
    return bad.map((b) => b.index);
}

/* ---------- Phase 2f — Ask Success Team ---------- */
export async function askSuccessTeam(payload) {
    const res = await http.post("/ask-success-team", payload);
    return res.data;
}

/* ---------- Phase 2e — Employee application (9-screen flow) ---------- */
export async function employeeApplyStart(inviteToken) {
    const res = await http.post(
        "/employee/apply/start",
        { invite_token: inviteToken },
        { withCredentials: true },
    );
    return res.data;
}
export async function employeeApplyGet() {
    const res = await http.get("/employee/apply", { withCredentials: true });
    return res.data;
}
export async function employeeApplyPatch(step, payload) {
    const res = await http.patch(
        `/employee/apply/step/${step}`,
        payload,
        { withCredentials: true },
    );
    return res.data;
}
export async function employeeApplyAdvance() {
    const res = await http.post("/employee/apply/advance", null, { withCredentials: true });
    return res.data;
}
export async function employeeApplyBack() {
    const res = await http.post("/employee/apply/back", null, { withCredentials: true });
    return res.data;
}
export async function employeeApplySubmit(payload) {
    const res = await http.post(
        "/employee/apply/submit",
        payload,
        { withCredentials: true },
    );
    return res.data;
}
export function employeeApplicationPdfUrl(applicationId) {
    return `${API}/employee/apply/${applicationId}/pdf`;
}

/* ---------- Phase 3a — Employer portal dashboard ---------- */
export async function portalEmployerDashboard(employerId) {
    const res = await http.get(`/portal/employer/${employerId}/dashboard`, {
        withCredentials: true,
    });
    return res.data;
}


/* ---------- Phase 3b — Employee portal auth + home ---------- */
// `requestEmployeeMagicLink` was retired in Phase A6. Use
// `employeePasswordSignin` (above) for all employee sign-in flows;
// /api/employee/request-magic-link now returns 404.

export async function employeePortalMe() {
    const res = await http.get("/employee/portal/me", { withCredentials: true });
    return res.data;
}

export async function employeePortalLogout() {
    const res = await http.post("/employee/logout", {}, { withCredentials: true });
    return res.data;
}

export async function employeePortalHome(employeeId) {
    const res = await http.get(`/portal/employee/${employeeId}/home`, {
        withCredentials: true,
    });
    return res.data;
}

export async function employeePortalRateProbe(employeeId, coverageCents) {
    const res = await http.post(
        `/portal/employee/${employeeId}/rate-probe`,
        { coverage_cents: coverageCents },
        { withCredentials: true },
    );
    return res.data;
}

/* ---------- Phase 3b §4 — Marketing waitlist (legacy — kept read-only) ---------- */
export async function joinMarketingWaitlist({ email, locale = "en" }) {
    const res = await http.post(
        "/marketing/employee-waitlist",
        { email, locale },
    );
    return res.data;
}

/* ---------- Phase 3d — "Send me the good stuff" ---------- */
/**
 * Capture a prospective-employee email and trigger the Mailgun-delivered
 * content email. Returns `{ ok: true }` on success; throws on 429 (rate
 * limit) or 4xx/5xx.
 */
export async function sendMeTheGoodStuff({
    email,
    locale = "en",
    referrer = null,
    utmSource = null,
    utmCampaign = null,
}) {
    const res = await http.post(
        "/prospective-employees/capture",
        {
            email,
            locale,
            referrer,
            utm_source: utmSource,
            utm_campaign: utmCampaign,
        },
    );
    return res.data;
}

/* ---------- Phase 3b §7 — Employer weekly digest ---------- */
export async function employerDigestSubscribe(employerId, subscribed) {
    const res = await http.post(
        `/portal/employer/${employerId}/digest/subscribe`,
        { subscribed },
        { withCredentials: true },
    );
    return res.data;
}


/* ---------- Phase 3c — Auth entry-point hardening ---------- */
export async function authEmailLookup({ email, context = null }) {
    const res = await http.post("/auth/email-lookup", { email, context });
    return res.data;
}

export async function submitAccessRequest({ email, name, role, note, locale = "en" }) {
    const res = await http.post(
        "/auth/access-request",
        { email, name, role: role || undefined, note: note || undefined, locale },
    );
    return res.data;
}

export async function listEmployerAdmins(employerId) {
    const res = await http.get(`/portal/employer/${employerId}/admins`, {
        withCredentials: true,
    });
    return res.data;
}

export async function addEmployerAdmin(employerId, { email, role }) {
    const res = await http.post(
        `/portal/employer/${employerId}/admins`,
        { email, role: role || undefined },
        { withCredentials: true },
    );
    return res.data;
}

export async function removeEmployerAdmin(employerId, email) {
    const res = await http.delete(
        `/portal/employer/${employerId}/admins/${encodeURIComponent(email)}`,
        { withCredentials: true },
    );
    return res.data;
}

export async function adminAccessRequests({ status = null, limit = 50 } = {}) {
    const res = await http.get("/admin/access-requests", {
        params: { status: status || undefined, limit },
        withCredentials: true,
    });
    return res.data;
}

